import React from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import {
	matchPath,
	Redirect,
	Route as ReactRoute,
} from 'react-router-dom';
import { Breadcrumb, HeaderBanner, Tooltip } from 'ljit-react-components';
import { omit } from 'lodash';
import { renderSwitches } from '.';
import { getTranslation as t } from '../../lib/i18n';
import { InfoWhiteSvg } from '../../images';

const propTypes = {
	path: PropTypes.string,
	exact: PropTypes.bool,
	strict: PropTypes.bool,
	component: PropTypes.any,
	paramProps: PropTypes.object,
	redirectPath: PropTypes.string,
	routes: PropTypes.arrayOf(PropTypes.object),
	meta: PropTypes.shape({
		breadcrumbName: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.node,
		]),
		isCrumbActive: PropTypes.bool,
		isCrumbVisible: PropTypes.bool,
		pageTitle: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.node,
		]),
		pageDescription: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.node,
		]),
	}),
	routePaths: PropTypes.array,
	extraProps: PropTypes.object,
};
const paramRule = /:(\w+)/g;
const paramReplace = (text, params = {}) => {
	return text.replace(paramRule, (_, key) => (params[key] || key));
};
const renderHeaderBannerTitle = (meta, match) => {
	const title = paramReplace(meta.pageTitle || t(meta.breadcrumbName), match.params);

	if (meta.titleInfoTooltip) {
		return (
			<>
				<span>{title}</span>
				<Tooltip
					title={t(meta.titleInfoTooltip)}
					mouseLeaveDelay={0.1}
					className="info-tooltip"
				>
					<span><img src={InfoWhiteSvg}/></span>
				</Tooltip>
			</>
		);
	}

	return title;
};

function getRouteMetaConfig(metaConfig) {
	const hasMetaConfig = Boolean(metaConfig);

	return Object.assign({}, {
		breadcrumbName: '',
		isCrumbActive: hasMetaConfig,
		isCrumbVisible: hasMetaConfig,
		pageTitle: '',
		pageDescription: '',
		isBannerVisible: hasMetaConfig,
	}, metaConfig);
}

const Route = props => {
	const routeProps = omit(props, [
		'routePaths',
		'extraProps',
		'component',
	]);
	const {
		extraProps,
		path,
		routes,
		paramProps,
		redirectPath,
		component: Page,
	} = props;
	const meta = { ...getRouteMetaConfig(props.meta) };

	return (
		<ReactRoute
			{...routeProps}
			render={matchProps => {
				const {
					match,
					history,
					location,
				} = matchProps;
				const { search } = location;
				const searchQuery = search ? qs.parse(search, { ignoreQueryPrefix: true }) : {};
				const mappedParamProps = {
					...searchQuery,
				};

				if (paramProps) {
					Object.keys(paramProps).forEach(key => {
						mappedParamProps[key] = paramReplace(paramProps[key], match.params);
					});
				}

				if (redirectPath) {
					if (matchPath(location.pathname, { path, exact: true })) {
						return <Redirect to={paramReplace(redirectPath, match.params)}/>;
					}
				}

				function onNavigate(uri, options = {}) {
					const { passProps = {}, replace = false } = options;
					const navigate = replace ? history.replace : history.push;

					navigate({
						pathname: uri,
						passProps,
					});
				}

				function onBack() {
					history.goBack();
				}

				// 單純地變換目前網址列中的網址（不導向新頁面）
				function onReplaceURL(path, state, pageTitle = '') {
					window.history.replaceState(state, pageTitle, path);
				}

				return (
					<>
						<Breadcrumb.BreadcrumbItem
							to={matchProps.match.url}
							isActive={meta.isCrumbActive}
							isVisible={meta.isCrumbVisible}
						>
							{paramReplace(t(meta.breadcrumbName), match.params)}
						</Breadcrumb.BreadcrumbItem>
						<HeaderBanner.HeaderBannerItem
							title={renderHeaderBannerTitle(meta, match)}
							description={meta.pageDescription}
							isBannerVisible={meta.isBannerVisible}
						/>
						<Page
							{...extraProps}
							{...location.passProps}
							{...mappedParamProps}
							onNavigate={onNavigate}
							onBack={onBack}
							onReplaceURL={onReplaceURL}
							renderedRoutes={renderSwitches(routes, extraProps)}
							pathName={location.pathname}
						/>
					</>
				);
			}}
		/>
	);
};

Route.propTypes = propTypes;

export default Route;
